// "Carros na oficina" — as colunas que o gestor escolhe e a ordem da lista.
//
// A mesma escolha vale para a tela do painel e para a exportação (PDF e
// planilha): duas escolhas separadas fariam o PDF sair diferente da tela sem
// ninguém entender por quê. O painel guarda a escolha no navegador e manda a
// lista na exportação (`GET /service-orders/in-shop/export?columns=…`).
//
// Onde está e placa não entram aqui: são fixas, porque sem elas a linha não
// diz de que carro está falando.

export const IN_SHOP_COLUMNS = [
  'vehicle',
  'customer',
  'customerPhone',
  'os',
  'status',
  'deadline',
  'providers',
  'mechanic',
  'enteredAt',
  'daysInShop',
] as const;
export type InShopColumn = (typeof IN_SHOP_COLUMNS)[number];

export const IN_SHOP_COLUMN_LABELS: Record<InShopColumn, string> = {
  vehicle: 'Veículo e cor',
  customer: 'Cliente',
  customerPhone: 'Telefone do cliente',
  os: 'OS',
  status: 'Status e etapa',
  deadline: 'Prazo',
  providers: 'Terceiros',
  mechanic: 'Mecânico',
  enteredAt: 'Entrou em',
  daysInShop: 'Na oficina há',
};

/** O que a tela mostrava antes da escolha, mais o prazo. */
export const DEFAULT_IN_SHOP_COLUMNS: readonly InShopColumn[] = [
  'vehicle',
  'customer',
  'os',
  'status',
  'deadline',
  'providers',
  'daysInShop',
];

/**
 * Lista vinda de fora (navegador, querystring) → colunas conhecidas, sem
 * repetição, na ordem do catálogo. A ordem das colunas é sempre a do
 * catálogo: quem marca e desmarca não espera que a coluna mude de lugar.
 */
export function normalizeInShopColumns(values: readonly unknown[]): InShopColumn[] {
  const wanted = new Set(values);
  return IN_SHOP_COLUMNS.filter((c) => wanted.has(c));
}

/** `location`: agrupado por lugar (o padrão). `deadline`: uma lista só, do mais atrasado ao mais longe. */
export const IN_SHOP_ORDERS = ['location', 'deadline'] as const;
export type InShopOrder = (typeof IN_SHOP_ORDERS)[number];

/**
 * Situação do prazo de um carro, no dia da oficina:
 *   - `late`: o prazo passou e o serviço ainda anda;
 *   - `today`: vence hoje e ainda não passou;
 *   - `on_time`: vence depois de hoje;
 *   - `none`: o serviço anda e a OS não tem prazo;
 *   - `done`: o serviço terminou (aguardando pagamento ou concluída). Não fica
 *     vermelho: o carro está pronto, e a demora ali é do cliente.
 */
export const IN_SHOP_DEADLINE_STATUSES = ['late', 'today', 'on_time', 'none', 'done'] as const;
export type InShopDeadlineStatus = (typeof IN_SHOP_DEADLINE_STATUSES)[number];

export interface InShopDeadlineSortable {
  deadlineStatus: InShopDeadlineStatus;
  /** ISO. */
  deadline: string | null;
  /** ISO. */
  enteredAt: string;
}

/** Faixa na ordem por prazo: com prazo e andando, depois sem prazo, depois serviço terminado. */
function deadlineRank(e: InShopDeadlineSortable): number {
  if (e.deadlineStatus === 'none') return 1;
  if (e.deadlineStatus === 'done') return 2;
  return 0;
}

/**
 * Ordem por prazo — "o que eu tenho que entregar primeiro?": do prazo mais
 * antigo (o mais atrasado) ao mais longe; depois quem não tem prazo e, no fim,
 * quem já terminou o serviço. Dentro das duas últimas faixas, quem está há
 * mais tempo primeiro. A tela e a exportação ordenam por aqui.
 */
export function compareInShopByDeadline(a: InShopDeadlineSortable, b: InShopDeadlineSortable): number {
  const byRank = deadlineRank(a) - deadlineRank(b);
  if (byRank !== 0) return byRank;
  if (deadlineRank(a) === 0 && a.deadline && b.deadline) {
    const byDeadline = a.deadline.localeCompare(b.deadline);
    if (byDeadline !== 0) return byDeadline;
  }
  return a.enteredAt.localeCompare(b.enteredAt);
}

/** "1 dia" / "3 dias". O zero ("hoje") fica com quem chama: cada tela escreve do seu jeito. */
export function formatDayCount(days: number): string {
  return days === 1 ? '1 dia' : `${days} dias`;
}

export interface InShopDoneInput {
  deadline: string | null;
  deadlineStatus: InShopDeadlineStatus;
  deadlineDaysLate: number;
  /** Quando o serviço terminou (entrada em aguardando pagamento ou concluída), ISO. */
  serviceDoneAt: string | null;
}

/**
 * O prazo de quem já terminou o serviço, em palavras — a mesma frase na
 * tela, no PDF e na planilha. Nulo quando o serviço anda ou não houve prazo.
 *
 * Compara com a hora em que o SERVIÇO terminou, não com `completed_at`: no
 * fluxo normal (execução → aguardando pagamento → concluída) a conclusão é
 * carimbada no pagamento, e a demora do cliente para pagar viraria atraso da
 * oficina. Terminou no dia do prazo mas depois da hora é "com atraso", como o
 * carro que ainda anda vira "atrasado" na hora em que o prazo passa.
 */
export function inShopDoneNote(e: InShopDoneInput): string | null {
  if (e.deadlineStatus !== 'done' || !e.deadline) return null;
  if (!e.serviceDoneAt) return 'serviço terminado';
  if (Date.parse(e.serviceDoneAt) <= Date.parse(e.deadline)) return 'pronto no prazo';
  return e.deadlineDaysLate > 0 ? `pronto ${formatDayCount(e.deadlineDaysLate)} depois` : 'pronto com atraso';
}
